'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import { SEARCH_DEBOUNCE_MS } from '@/constants/microsoftCategories'

interface CatalogSearchProps {
  onChange: (query: string) => void
}

export default function CatalogSearch({ onChange }: CatalogSearchProps) {
  const [draft, setDraft] = useState('')
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value
      setDraft(next)

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        onChange(next)
      }, SEARCH_DEBOUNCE_MS)
    },
    [onChange],
  )

  const handleClear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setDraft('')
    onChange('')
    inputRef.current?.focus()
  }, [onChange])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  useEffect(() => {
    const focusOnSlash = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      // Typing a slash into another field must still type a slash
      const target = e.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
      e.preventDefault()
      inputRef.current?.focus()
    }
    window.addEventListener('keydown', focusOnSlash)
    return () => window.removeEventListener('keydown', focusOnSlash)
  }, [])

  return (
    <div className="relative w-full">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
        <svg
          className="h-4 w-4 text-primary-ink-60"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      </div>
      <input
        ref={inputRef}
        type="search"
        value={draft}
        onChange={handleChange}
        placeholder="Search all products"
        aria-label="Search Microsoft products"
        aria-keyshortcuts="/"
        className="search-reset h-10 w-full rounded-md border border-primary-ink-10 bg-white pl-10 pr-10 text-sm text-primary-ink-100 transition-colors placeholder:text-primary-ink-60 hover:border-primary-ink-30 focus:rounded-md focus:border-primary-ink-100 focus:outline-none focus:ring-0 motion-reduce:transition-none"
      />
      {draft && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary-ink-60 hover:text-primary-ink-100"
          aria-label="Clear search">
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  )
}
